import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { navItems, type NavItem } from '~/app/components/navigation/nav-items';
import { SidebarBrand } from '~/app/components/sideBar/SidebarBrand';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';

function pathMatches(href: string, path: string): boolean {
  return path === href || path.startsWith(`${href}/`);
}

function resolveActiveParent(
  path: string,
  visibleNavItems: NavItem[],
  visibleChildrenByParent: Map<string, NavItem['children']>,
): NavItem | null {
  for (const item of visibleNavItems) {
    const children = visibleChildrenByParent.get(item.labelKey);
    if (!children?.length) {
      continue;
    }
    const hit = [...children]
      .sort((a, b) => b.href.length - a.href.length)
      .find((c) => pathMatches(c.href, path));
    if (hit) {
      return item;
    }
  }
  return null;
}

function resolveSelectedLeafKey(
  path: string,
  visibleNavItems: NavItem[],
  visibleChildrenByParent: Map<string, NavItem['children']>,
): string {
  for (const item of visibleNavItems) {
    const children = visibleChildrenByParent.get(item.labelKey);
    if (children?.length) {
      const hit = [...children]
        .sort((a, b) => b.href.length - a.href.length)
        .find((c) => pathMatches(c.href, path));
      if (hit) {
        return hit.href;
      }
    }
  }
  const top = [...visibleNavItems]
    .filter((i) => !i.children?.length)
    .sort((a, b) => b.href.length - a.href.length)
    .find((i) => pathMatches(i.href, path));
  return top?.href ?? '/home';
}

export function Side({
  collapsed,
  onCollapsedChange,
}: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}) {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const path = pathname || '/';
  const { user } = useAuth();
  const { hasAllPermissions } = usePermission();
  const organizationName = user?.organizationName ?? 'Evimo CPO';
  const organizationLabel = t('organization');
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const initials = organizationName?.slice(0, 2).toUpperCase();

  const [expandedSectionKey, setExpandedSectionKey] = useState<string | null>(
    null,
  );

  const visibleNavItems = useMemo(() => {
    const role = user?.roleName;
    return navItems.filter((item) => {
      if (
        item.visibleForRoles?.length &&
        (!role || !item.visibleForRoles.includes(role))
      ) {
        return false;
      }
      if (item.hiddenForRoles && role && item.hiddenForRoles.includes(role)) {
        return false;
      }
      if (
        item.requiredPermissions?.length &&
        !hasAllPermissions(item.requiredPermissions)
      ) {
        return false;
      }
      return true;
    });
  }, [hasAllPermissions, user?.roleName]);

  const visibleChildrenByParent = useMemo(() => {
    const role = user?.roleName;
    const map = new Map<string, (typeof navItems)[number]['children']>();

    for (const item of visibleNavItems) {
      if (!item.children?.length) {
        continue;
      }

      const children = item.children.filter((child) => {
        if (
          child.visibleForRoles?.length &&
          (!role || !child.visibleForRoles.includes(role))
        ) {
          return false;
        }
        if (
          child.hiddenForRoles &&
          role &&
          child.hiddenForRoles.includes(role)
        ) {
          return false;
        }
        return true;
      });

      map.set(item.labelKey, children);
    }

    return map;
  }, [user?.roleName, visibleNavItems]);

  const activeParentFromPath = useMemo(
    () => resolveActiveParent(path, visibleNavItems, visibleChildrenByParent),
    [path, visibleNavItems, visibleChildrenByParent],
  );

  useEffect(() => {
    if (activeParentFromPath) {
      setExpandedSectionKey(activeParentFromPath.labelKey);
      return;
    }
    setExpandedSectionKey(null);
  }, [activeParentFromPath]);

  const selectedKey = useMemo(
    () =>
      resolveSelectedLeafKey(path, visibleNavItems, visibleChildrenByParent),
    [path, visibleNavItems, visibleChildrenByParent],
  );

  const navItemClass = (item: NavItem): string => {
    const children = visibleChildrenByParent.get(item.labelKey);
    const hasChildren = !!children?.length;
    const isSectionExpanded = expandedSectionKey === item.labelKey;
    const isParentActive = activeParentFromPath?.labelKey === item.labelKey;
    const isLeafActive = !hasChildren && pathMatches(item.href, path);
    const classes = ['evimo-nav-item', 'evimo-nav-item--root'];
    if (hasChildren && isSectionExpanded && !collapsed) {
      classes.push('is-open');
    }
    if (isLeafActive || isParentActive) {
      classes.push('is-current');
    }
    return classes.join(' ');
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    onCollapsedChange(next);
    if (!next && activeParentFromPath) {
      setExpandedSectionKey(activeParentFromPath.labelKey);
    }
  };

  const toggleSection = (item: NavItem) => {
    const children = visibleChildrenByParent.get(item.labelKey);
    if (!children?.length) {
      return;
    }

    if (collapsed) {
      onCollapsedChange(false);
      setExpandedSectionKey(item.labelKey);
      const onChild = children.some((c) => pathMatches(c.href, path));
      if (!onChild) {
        void navigate(children[0].href);
      }
      return;
    }

    setExpandedSectionKey((prev) =>
      prev === item.labelKey ? null : item.labelKey,
    );
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className="evimo-nav-column"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <SidebarBrand collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />

      <aside className="evimo-nav-panel" aria-label={t('navigation')}>
        {!isSuperAdmin && (
          <button
            type="button"
            className="evimo-nav-org"
            title={organizationName}
            aria-label={organizationName}
          >
            <span className="evimo-nav-org__mark">{initials}</span>
            {!collapsed && (
              <span className="evimo-nav-org__text">
                <span className="evimo-nav-org__name">{organizationName}</span>
                <span className="evimo-nav-org__meta">{organizationLabel}</span>
              </span>
            )}
          </button>
        )}

        <nav className="evimo-nav-scroll" aria-label={t('navigation')}>
          {visibleNavItems.map((item) => {
            const children = visibleChildrenByParent.get(item.labelKey);
            const hasChildren = !!children?.length;
            const label = t(item.labelKey);
            const isExpanded =
              !collapsed && expandedSectionKey === item.labelKey;

            if (hasChildren) {
              return (
                <div key={item.labelKey} className="evimo-nav-section">
                  <button
                    type="button"
                    className={navItemClass(item)}
                    aria-expanded={isExpanded}
                    aria-label={label}
                    title={collapsed ? label : undefined}
                    onClick={() => toggleSection(item)}
                  >
                    <span className="evimo-nav-item__icon">{item.icon}</span>
                    <span className="evimo-nav-item__label">{label}</span>
                    {!collapsed ? (
                      <DownOutlined
                        className={`evimo-nav-item__chevron${isExpanded ? ' is-expanded' : ''}`}
                        aria-hidden
                      />
                    ) : null}
                  </button>

                  {isExpanded ? (
                    <div className="evimo-nav-section__children">
                      {children!.map((child) => {
                        const childLabel = t(child.labelKey);
                        const isCurrent = selectedKey === child.href;
                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`evimo-nav-item evimo-nav-item--child${isCurrent ? ' is-current' : ''}`}
                            aria-current={isCurrent ? 'page' : undefined}
                          >
                            {childLabel}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            }

            return (
              <Link
                key={item.labelKey}
                to={item.href}
                className={navItemClass(item)}
                aria-label={label}
                title={collapsed ? label : undefined}
                aria-current={pathMatches(item.href, path) ? 'page' : undefined}
                onClick={() => setExpandedSectionKey(null)}
              >
                <span className="evimo-nav-item__icon">{item.icon}</span>
                <span className="evimo-nav-item__label">{label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}
